import { Inject, Injectable } from '@angular/core';
import { NB_AUTH_STRATEGIES, NbAuthResult, NbAuthService, NbTokenService } from '@nebular/auth';
import { Observable } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';

/**
 * NbAuthService that sends one refresh-token request at a time.
 *
 * When the access token has expired, every request that is in flight (or started by the route guard)
 * asks Nebular for a refresh on its own: a screen that loads twenty things at once fired twenty
 * identical /auth/refresh-token calls (bursts of up to 50 in 10 seconds were seen on the dev API), each
 * overwriting the stored token. Callers that arrive while a refresh is running now wait for that same
 * result; a refresh needed later (the next expiry) starts a new request as before.
 */
@Injectable()
export class SharedRefreshAuthService extends NbAuthService {

  private readonly inFlight = new Map<string, Observable<NbAuthResult>>();

  constructor(tokenService: NbTokenService, @Inject(NB_AUTH_STRATEGIES) strategies: any) {
    super(tokenService, strategies);
  }

  refreshToken(strategyName: string, data?: any): Observable<NbAuthResult> {
    let shared = this.inFlight.get(strategyName);
    if (!shared) {
      shared = super.refreshToken(strategyName, data).pipe(
        finalize(() => this.inFlight.delete(strategyName)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.inFlight.set(strategyName, shared);
    }
    return shared;
  }
}
